// src/lib/dispenserView.ts
//
// Pure reading rules of the «Dispensadores» screen, kept out of the component
// so they are tested once. Nothing here estimates: a figure the API did not
// measure is `null`, which the screen renders as the dash placeholder.
import type { DispenserSummary } from '@/types/dispensers';

/** The user's hotels have at least one registered dispenser. */
export const hasDispensersInScope = (summary: DispenserSummary | null | undefined): boolean =>
  (summary?.dispensers.total ?? 0) > 0;

/**
 * A success rate only when something was attempted: the API answers 0 for a
 * period with no delivery attempt, which is "nothing happened", not
 * "everything failed". `delivered + failed` = the attempts.
 */
export const measuredSuccessRate = (delivered: number, failed: number, rate: number): number | null =>
  delivered + failed > 0 ? rate : null;

export interface ErrorTypeRow {
  code: string;
  /** Error events with this code. */
  count: number;
  /** Failed delivery attempts this code was a cause of (P05). */
  failed: number;
}

/** Errors by code, most frequent first (ties by code), zero counts dropped; each with the
 *  failed deliveries it caused. */
export const errorTypeRows = (
  byCode: Record<string, number> | null | undefined,
  failedByCause?: Record<string, number> | null
): ErrorTypeRow[] =>
  Object.entries(byCode ?? {})
    .filter(([, count]) => count > 0)
    .map(([code, count]) => ({ code, count, failed: failedByCause?.[code] ?? 0 }))
    .sort((a, b) => b.count - a.count || a.code.localeCompare(b.code));

/** Code an error without one is reported under. */
export const NO_ERROR_CODE = 'NO_CODE';
